import { forwardRef, type ForwardedRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface LabelChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** Dot color (any CSS color). Pair with the shared labelColor() for type tallies. */
  color?: string;
  label: ReactNode;
  /** Optional trailing count, e.g. a per-type tally. */
  count?: number;
}

/**
 * LabelChip — a small colored-dot + label (+ count) chip. The atom behind an
 * element-type tally ("Title ×2 · Text ×11 · Table ×1"); color is passed in so
 * it can match the overlay palette (labelColor).
 */
export const LabelChip = forwardRef(function LabelChip(
  { className, color, label, count, ...props }: LabelChipProps,
  ref: ForwardedRef<HTMLSpanElement>,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-md border border-border bg-card px-2 text-[11px] text-foreground',
        className,
      )}
      {...props}
    >
      {color ? <span className="size-2 shrink-0 rounded-[3px]" style={{ background: color }} aria-hidden /> : null}
      <span className="font-medium">{label}</span>
      {count != null ? <span className="font-mono text-[10px] text-muted-foreground">{count}</span> : null}
    </span>
  );
});
